
/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');
/**
 * @typedef {Object} DraggableOptions
 * @property {boolean} [limitToOwnCollection] - If true, items can only be dropped within the same parent container, preventing cross-container dragging. This is useful for maintaining logical groupings of items.
 * @property {boolean} [disabled] - If true, the draggable behavior will not be initialized, preventing any reordering of items. This is useful for locked or read-only states.
 * @property {string} [elementSelector] - CSS selector for the draggable element. If not provided, the entire container element will be draggable.
 * @property {function(HTMLElement, HTMLElement): void} [onDrag] - Callback function that is called continuously while dragging. Receives the dragged element and the current event target as arguments.
 * @property {function(HTMLElement, HTMLElement): void} [onDragStart] - Callback function that is called when dragging starts. Receives the dragged element and the original event target as arguments.
 * @property {function(HTMLElement, HTMLElement): void} [onDragEnter] - Callback function that is called when the dragged element enters a valid drop target. Receives the dragged element and the drop target as arguments.
 * @property {function(HTMLElement, HTMLElement): void} [onDragOver] - Callback function that is called when the dragged element is moved over a valid drop target. Receives the dragged element and the current event target as arguments.
 * @property {function(HTMLElement, HTMLElement): void} [onDragLeave] - Callback function that is called when the dragged element leaves a valid drop target. Receives the dragged element and the drop target as arguments.
 * @property {function(HTMLElement, HTMLElement): void} [onDragEnd] - Callback function that is called when dragging ends. Receives the dragged element and the original event target as arguments.
 * @property {function(HTMLElement, HTMLElement): void} [onDrop] - Callback function that is called when the dragged element is dropped. Receives the dragged element and the drop target as arguments.
*/

class DraggableUtil extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
		this.customCSSInjected = false;
		this.animationDuration = 500; // default duration for any animations related to dragging (e.g., shake animation when drop is denied)
		this.outlineThickness = 3; // default thickness for outlines when dragging over valid drop targets
	}

	/**
	 * Inject the custom CSS styles for the draggable behavior into the document head. This includes styles for the draggable items, drag-over states,
	 * and any animations for denied drops. The method checks if the styles have already been injected to avoid duplicates.
	 * @returns {void}
	 */
	injectCustomCSS()
	{
		if (this.customCSSInjected || document.getElementById('DraggableUtil-Styles')) return;

		const styleElement = document.createElement('style');
		styleElement.id = 'DraggableUtil-Styles';
		styleElement.textContent = /*css*/`
			/* Draggable item styles */
			[draggable="true"] {
				cursor: move;
				transition: transform 150ms ease, border-color 150ms ease;
			}
			/* Optional drag class for styling the dragged element */
			.is-dragging {
				opacity: 0.5;
			}
			/* Optional class for styling drop targets when an item is dragged over them */
			.is-drag-over {
				border: calc(${this.outlineThickness}px) solid var(--bulma-info);
				transform: scale(1.1, 1.1);
			}
			/* Optional class for styling drop targets when a drop is denied - shakes */
			.is-drag-over-denied {
				border: calc(${this.outlineThickness}px) solid var(--bulma-danger);
			}
			/* Optional class for styling the drop container when a drop is denied - shakes */
			.is-drag-over-denied-collection {
				animation: shake ${this.animationDuration}ms ease;
			}
			@keyframes shake {
				0% { transform: translate(0, 0) }
				25% { transform: translate(-5px, 0) }
				50% { transform: translate(5px, 0) }
				75% { transform: translate(-5px, 0) }
				100% { transform: translate(0, 0) }	
			}`;
		document.head.appendChild(styleElement);
		this.customCSSInjected = true;
	}

	/**
	 * Initializes the draggable behavior on a given element with specified options.
	 * @param {HTMLElement} containerElement - The container element that holds the draggable items.
	 * @param {DraggableOptions} options - Configuration options for the draggable behavior.
	 */
	initializeDraggableCollection(containerElement, options)
	{
		// If disabled option is set, do not initialize draggable behavior (these are for locked/readonly states)
		if (options.disabled)
		{
			this.pict.log.info('DraggableUtil: Draggable behavior is prevented by options, skipping initialization.');
			return;
		}
		if (!containerElement)
		{
			this.pict.log.warn('DraggableUtil: No container element provided for initializing draggable collection.');
			return;
		}
		if (options.elementSelector && typeof options.elementSelector !== 'string')
		{
			this.pict.log.warn('DraggableUtil: Invalid elementSelector provided in options. Expected a string CSS selector. Draggable behavior will not be initialized.');
			return;
		}
		this.injectCustomCSS();

		// make each item in the container that matches the elementSelector draggable and apply the necessary event listeners for drag-and-drop interactions
		const draggableItems = containerElement.querySelectorAll(options.elementSelector);
		draggableItems.forEach((item) =>
		{
			this.makeElementDraggable(/** @type {HTMLElement} */ (item), options);
		});
	}

	/**
	 * Makes a single element draggable and applies the necessary event listeners for drag-and-drop interactions.
	 * @param {HTMLElement|String} pElement - The element node or selector string to be made draggable.
	 * @param {DraggableOptions} pOptions - Configuration options for the draggable behavior.
	 */
	makeElementDraggable(pElement, pOptions)
	{
		if (pOptions.disabled)
		{
			this.pict.log.info('DraggableUtil: Draggable behavior is disabled by options, skipping initialization.');
			return;
		}
		const tmpElementNode = typeof pElement === 'string' ? document.querySelector(pElement) : pElement;
		if (!tmpElementNode)
		{
			this.pict.log.warn('DraggableUtil: No element found for making draggable with selector:', pElement);
			return;
		}
		tmpElementNode.setAttribute('draggable', 'true');
		tmpElementNode.classList.add('is-draggable');
		tmpElementNode.setAttribute('data-draggable-id', `draggable-${Math.random().toString(36).substr(2, 9)}`); // Assign a unique ID for tracking
		this.applyEventListenersToCard(/** @type {HTMLElement} */ (tmpElementNode), pOptions);
	}

	/**
	 * Applies the necessary event listeners to a draggable item for handling drag-and-drop interactions.
	 * @param {HTMLElement} item - The draggable item element.
	 * @param {DraggableOptions} options - Configuration options for the draggable behavior.
	 */
	applyEventListenersToCard(item, options)
	{
		item.addEventListener('dragstart', (/** @type {DragEvent} */ event) =>
		{
			event.stopPropagation(); // Prevent drag event from bubbling up to parent containers which may also be draggable

			this._dragSourceElement = item;
			event.dataTransfer.effectAllowed = 'move';
			event.dataTransfer.dropEffect = 'move';
			event.dataTransfer.setData('text/plain', item.getAttribute('data-draggable-id')); // Store the unique ID of the dragged element for later reference (e.g., to identify the dragged element during drop)
			event.dataTransfer.setData('text/html', ''); // Required for Firefox compatibility
			item.classList.add('is-dragging');

			if (options.onDragStart)
			{
				options.onDragStart(item, /** @type {HTMLElement} */ (event.currentTarget));
			}
		}, false);

		item.addEventListener('dragenter', (/** @type {DragEvent} */ event) =>
		{
			event.preventDefault();
			event.stopPropagation(); // Prevent drag event from bubbling up to parent containers which may also be draggable

			/** @type {HTMLElement} */
			const target = /** @type {HTMLElement} */ (event.currentTarget);
			const isSameContainer = this._dragSourceElement.parentElement === target.parentElement;
			// if limitToOwnCollection option is set, only allow drag enter if the target is in the same parent container as the drag source (this is to prevent dragging items between different collections/containers which may have different rules or constraints)
			if (options.limitToOwnCollection && !isSameContainer)
			{
				this.pict.log.info('DraggableUtil: Drag enter ignored due to limitToOwnCollection option and different parent containers.');
				return;
			}
			target.classList.add('is-drag-over');

			if (options.onDragEnter)
			{
				options.onDragEnter(item, target);
			}
		}, false);

		item.addEventListener('dragover', (/** @type {DragEvent} */ event) =>
		{
			event.preventDefault(); // Necessary to allow dropping
			event.stopPropagation(); // Prevent drag event from bubbling up to parent containers which may also be draggable

			/** @type {HTMLElement} */
			const target = /** @type {HTMLElement} */ (event.currentTarget);
			target.classList.add('is-drag-over'); // Ensure the drag-over class is applied while dragging over the target

			if (options.onDragOver)
			{
				options.onDragOver(item, target);
			}
		}, false);

		item.addEventListener('dragleave', (/** @type {DragEvent} */ event) =>
		{
			event.preventDefault();
			event.stopPropagation(); // Prevent drag event from bubbling up to parent containers which may also be draggable

			/** @type {HTMLElement} */
			const target = /** @type {HTMLElement} */ (event.currentTarget);
			target.classList.remove('is-drag-over');

			if (options.onDragLeave)
			{
				options.onDragLeave(item, target);
			}
		}, false);

		item.addEventListener('drag', (/** @type {DragEvent} */ event) =>
		{
			event.stopPropagation(); // Prevent drag event from bubbling up to parent containers which may also be draggable

			/** @type {HTMLElement} */
			const target = /** @type {HTMLElement} */ (event.currentTarget);
			target.classList.add('is-skeleton'); // Hide the original element while dragging to allow the drag image to be visible (especially if using a custom drag image)

			if (options.onDrag)
			{
				options.onDrag(item, target);
			}
		}, false);

		item.addEventListener('drop', (/** @type {DragEvent} */ event) =>
		{
			event.preventDefault();
			event.stopPropagation(); // Prevent drag event from bubbling up to parent containers which may also be draggable

			this._dragSourceElement.classList.remove('is-skeleton'); // Unhide the original element after dropping

			// if the drop target is in the same collection as the drag source, we can allow dropping and reordering within the same container,
			// otherwise, we ignore the drop (this is to prevent dragging items between different collections/containers which may have different rules or constraints)
			if (item !== this._dragSourceElement)
			{
				/** @type {HTMLElement} */
				const target = /** @type {HTMLElement} */ (event.currentTarget);
				target.classList.remove('is-drag-over');

				// if limitToOwnCollection option is set, check if the drop target is in the same parent container as the drag source before allowing the drop
				if (options.limitToOwnCollection && item.parentElement !== this._dragSourceElement.parentElement)
				{
					this.pict.log.info('DraggableUtil: Drop ignored due to limitToOwnCollection option and different parent containers.');
					item.classList.remove('is-drag-over');
					this._dragSourceElement.classList.add('is-drag-over-denied');
					this._dragSourceElement.classList.remove('is-dragging');
					const sourceElement = this._dragSourceElement; // Store reference to the dragged element for use in the timeout callback
					setTimeout(() => sourceElement?.classList?.remove('is-drag-over-denied'), this.animationDuration);
					// find the parent container of the dropped element and apply a shake animation to indicate the drop was denied (optional, can be styled with CSS)
					const parentContainer = item.parentElement;
					if (parentContainer)
					{
						parentContainer.classList.add('is-drag-over-denied-collection');
						setTimeout(() => parentContainer.classList.remove('is-drag-over-denied-collection'), this.animationDuration);
					}
					return;
				}
				item.classList.remove('is-drag-over');
				// Move the dragged item to its new position in the DOM (before or after the drop target depending on cursor position within the target)
				const rect = item.getBoundingClientRect();
				const midpoint = rect.left + rect.width / 2;
				if (event.clientX > midpoint)
				{
					item.parentNode.insertBefore(this._dragSourceElement, item.nextSibling);
				}
				else
				{
					item.parentNode.insertBefore(this._dragSourceElement, item);
				}

				if (options.onDrop)
				{
					options.onDrop(item, /** @type {HTMLElement} */ (event.currentTarget));
				}
			}
		}, false);

		item.addEventListener('dragend', (/** @type {DragEvent} */ event) =>
		{
			event.preventDefault();
			event.stopPropagation(); // Prevent drag event from bubbling up to parent containers which may also be draggable

			item.classList.remove('is-dragging');
			item.classList.remove('is-skeleton');
			// Clean up any remaining drag-over classes on potential drop targets after dragging ends
			const dropTargets = document.querySelectorAll('.is-drag-over');
			dropTargets.forEach((target) => target.classList.remove('is-drag-over'));

			if (options.onDragEnd)
			{
				options.onDragEnd(item, /** @type {HTMLElement} */ (event.currentTarget));
			}
			this._dragSourceElement = null;
		}, false);
	}
}

module.exports = DraggableUtil;
